import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { PanelMode } from './floating-panel.types';

/**
 * Generic floating panel shell anchored to the bottom of its positioned parent.
 *
 * @remarks
 * The parent element must have `position: relative` for absolute positioning to work.
 * Internal content is projected via `ng-content`. The panel manages its own mode state;
 * the parent only needs to set the initial value.
 */
@Component({
  selector: 'lib-floating-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconButton, MatIcon, MatDivider],
  templateUrl: './floating-panel.component.html',
  styleUrl: './floating-panel.component.scss',
  host: {
    '[class.panel--dock]': 'isDock()',
    '[class.panel--fullscreen]': 'isFullscreen()',
    '[class.panel--collapsed]': 'isCollapsed()',
  },
})
export class FloatingPanelComponent implements OnInit {
  readonly title = input<string>('');
  readonly subtitle = input<string>('');
  readonly initialMode = input<PanelMode>('dock');

  readonly closed = output<void>();
  readonly modeChanged = output<PanelMode>();

  readonly _mode = signal<PanelMode>('dock');

  readonly isDock = computed(() => this._mode() === 'dock');
  readonly isFullscreen = computed(() => this._mode() === 'fullscreen');
  readonly isCollapsed = computed(() => this._mode() === 'collapsed');

  ngOnInit(): void {
    this._mode.set(this.initialMode());
  }

  // "dock" is the only normal/base mode — fullscreen and collapsed are both transient overlays
  // that always toggle back to it, never to each other. (A single shared "previous mode" used to
  // track this instead, but toggling fullscreen then collapsed then back would restore fullscreen
  // instead of dock, since each toggle blindly overwrote it with whatever mode was current.)
  toggleFullscreen(): void {
    this._changeMode(this.isFullscreen() ? 'dock' : 'fullscreen');
  }

  toggleCollapsed(): void {
    this._changeMode(this.isCollapsed() ? 'dock' : 'collapsed');
  }

  close(): void {
    this.closed.emit();
  }

  private _changeMode(mode: PanelMode): void {
    this._mode.set(mode);
    this.modeChanged.emit(mode);
  }
}
